import { motion } from "framer-motion";
import { Check } from "lucide-react";
import type { NihaoBridgeContent } from "../content/types";

interface PricingSectionProps {
  content: NihaoBridgeContent["pricing"];
}

export default function PricingSection({ content }: PricingSectionProps) {
  return (
    <section
      className="w-full py-20 px-[5%] bg-gradient-to-b from-slate-50/50 to-slate-100/30"
      id="pricing"
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-3xl lg:text-4xl font-bold mb-4 text-center text-slate-900"
        >
          {content.title}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-center text-slate-600 mb-12"
        >
          {content.subtitle}
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className={`rounded-xl p-8 text-center relative transition-all duration-300 ${
                plan.isPopular
                  ? "bg-white text-slate-900 shadow-xl border-2 border-cyan-500"
                  : "bg-white shadow-lg hover:shadow-xl border border-slate-200/60 hover:border-cyan-400/30"
              }`}
            >
              {/* Popular badge */}
              {plan.isPopular && plan.popularLabel && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {plan.popularLabel}
                </div>
              )}

              <h3
                className={`text-2xl font-bold mb-2 ${
                  plan.isPopular ? "mt-2" : ""
                }`}
              >
                {plan.name}
              </h3>

              <div className="text-4xl font-bold mb-4 text-cyan-500">
                {plan.price}
                <span className="text-base font-normal">
                  {plan.currency}
                  {plan.period}
                </span>
              </div>

              <p className="mb-6 pb-6 border-b border-slate-200 text-slate-600">
                {plan.description}
              </p>

              <ul className="text-left space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 text-emerald-500" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center text-slate-500 text-sm mt-8"
        >
          {content.footnote}
        </motion.p>
      </div>
    </section>
  );
}
